import { useTranslation } from 'react-i18next'
import { Building, Lock, Users } from '@digital-twins/design-system/icons'
import {
  DEFAULT_LIBRARY_COLLECTION_SHARING_MODE,
  LibraryCollectionSharingMode,
} from '@digital-twins/shared-types'

import { cn } from '@/lib/utils'

const SHARING_ICONS: Record<LibraryCollectionSharingMode, typeof Lock> = {
  private: Lock,
  shared: Users,
  organization: Building,
}

const SHARING_LABEL_KEYS = {
  private: 'library.collectionSharing.private',
  shared: 'library.collectionSharing.shared',
  organization: 'library.collectionSharing.organization',
} as const satisfies Record<LibraryCollectionSharingMode, string>

/** The i18n key for a sharing mode's label: Only me, Specific people, Whole org. */
export function collectionSharingLabelKey(mode: LibraryCollectionSharingMode) {
  return SHARING_LABEL_KEYS[mode]
}

export interface CollectionSharingBadgeProps {
  sharingMode: LibraryCollectionSharingMode
  /** Icon only (the rail) or icon and label (a banner, a card). */
  showLabel?: boolean
  /** Visible label for one mode, in place of the default. The icon stays. */
  labelFor?: Partial<Record<LibraryCollectionSharingMode, string>>
  className?: string
  testId?: string
}

/** Who can see a collection: Only me, Specific people or Whole org. */
export function CollectionSharingBadge(props: CollectionSharingBadgeProps) {
  const { sharingMode, showLabel = false, labelFor, className, testId } = props

  const { t } = useTranslation('app')

  // A payload from before sharing existed reads as the backfilled default.
  const mode = SHARING_ICONS[sharingMode] ? sharingMode : DEFAULT_LIBRARY_COLLECTION_SHARING_MODE
  const Icon = SHARING_ICONS[mode]
  const label = labelFor?.[mode] ?? t(SHARING_LABEL_KEYS[mode])

  return (
    <span
      className={cn('inline-flex shrink-0 items-center gap-1 text-[12px] text-muted-foreground', className)}
      title={label}
      data-testid={testId}
      data-sharing-mode={mode}
    >
      <Icon className="h-3 w-3 shrink-0" strokeWidth={2} aria-hidden="true" />
      {showLabel ? <span>{label}</span> : <span className="sr-only">{label}</span>}
    </span>
  )
}
